import React, { useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Stack,
  Chip,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PhoneInTalkRoundedIcon from "@mui/icons-material/PhoneInTalkRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import { useDispatch, useSelector } from "src/redux/store";
import { getAddOns } from "src/redux/slices/addOns";

// Selector EXCLUSIVO de nivel de soporte (radio, no checkbox). Tres
// tarjetas: Básico (incluido, no toggleable) + Estándar + Prioritario.
// Los precios de Estándar y Prioritario los lee del catálogo de add-ons
// filtrando por `kind`. Si el fundador aún no ha creado esos add-ons,
// la tarjeta se muestra deshabilitada con "Próximamente".
//
// Uso:
//   <SupportPicker
//     value={supportKind}
//     onChange={setSupportKind}
//     pendingActivationKind={...}   // kind que se activará en pendingAt
//     pendingCancelKind={...}       // kind que se cancelará en pendingAt
//     pendingAt={dateString}        // fecha del cambio programado
//     isCurrentPaid                 // pinta chip "Plan actual" en value
//   />
//     value: null | "support-standard" | "support-priority"

const TIERS = [
  {
    key: "basic",
    kind: null, // no matchea add-on — es el "por defecto" gratis
    title: "Básico",
    tagline: "Incluido en cualquier plan",
    channel: "Email / ticket",
    responseTime: "24-48h laborables",
    hours: "L-V 9-18",
    icon: EmailRoundedIcon,
    color: "info",
  },
  {
    key: "standard",
    kind: "support-standard",
    title: "Estándar",
    tagline: "Fin de semana cubierto",
    channel: "WhatsApp prioritario",
    responseTime: "< 4h laborables",
    hours: "L-S 9-22",
    icon: WhatsAppIcon,
    color: "success",
  },
  {
    key: "priority",
    kind: "support-priority",
    title: "Prioritario",
    tagline: "24/7 con SLA por escrito",
    channel: "Teléfono directo",
    responseTime: "< 1h",
    hours: "7 días, 8-24 (festivos)",
    icon: PhoneInTalkRoundedIcon,
    color: "primary",
    badge: "Cupo limitado",
  },
];

export default function SupportPicker({
  value,
  onChange,
  pendingActivationKind = null,
  pendingCancelKind = null,
  pendingAt = null,
  isCurrentPaid = false,
  cycle = "monthly",
}) {
  const dispatch = useDispatch();
  const { addOns } = useSelector((s) => s.addOns);

  useEffect(() => {
    if (!addOns || addOns.length === 0) dispatch(getAddOns(true));
  }, [dispatch, addOns]);

  // El catálogo tiene 2 filas por kind de soporte (mensual + anual). El
  // precio y el disponibility del picker corresponden al ciclo del plan
  // del user — el backend hace swap silencioso al cambiar de plan vía
  // sibling. Sin este filtro, `find` cogía cualquiera de los dos y podía
  // pintar el precio anual (×10) como si fuera mensual.
  const priceFor = (kind) => {
    if (!kind) return null;
    const match = addOns?.find(
      (a) => a.kind === kind && (a.billingCycle || "monthly") === cycle,
    );
    return match?.price;
  };

  const isSelected = (tier) => {
    if (tier.key === "basic") return !value; // Básico = ausencia de selección de soporte pagado
    return value === tier.kind;
  };

  const handleClick = (tier) => {
    if (tier.key === "basic") {
      onChange(null);
      return;
    }
    const price = priceFor(tier.kind);
    if (price == null) return; // add-on no configurado aún → no toggleable
    // Toggle: si ya está seleccionado, volver a Básico.
    onChange(value === tier.kind ? null : tier.kind);
  };

  const formatPendingDate = (d) => {
    if (!d) return "próximamente";
    try {
      return new Date(d).toLocaleDateString("es-ES", { day: "2-digit", month: "long" });
    } catch (_) {
      return "próximamente";
    }
  };

  return (
    <Grid container spacing={2}>
      {TIERS.map((tier) => {
        const price = tier.kind ? priceFor(tier.kind) : 0;
        const selected = isSelected(tier);
        const unavailable = tier.kind && price == null;
        const isCurrent = isCurrentPaid && selected;
        const isPendingActivation =
          !!pendingActivationKind && tier.kind === pendingActivationKind;
        const isPendingCancel =
          !!pendingCancelKind &&
          (tier.kind === pendingCancelKind ||
            (tier.key === "basic" && pendingCancelKind && !pendingActivationKind));
        const IconComp = tier.icon;
        return (
          <Grid item xs={12} md={4} key={tier.key}>
            <Card
              onClick={() => !unavailable && handleClick(tier)}
              sx={{
                position: "relative",
                cursor: unavailable ? "not-allowed" : "pointer",
                borderRadius: 2,
                border: "2px solid",
                borderColor: selected
                  ? `${tier.color}.main`
                  : isPendingActivation
                  ? "info.main"
                  : "divider",
                bgcolor: selected
                  ? `${tier.color}.lighter`
                  : isPendingActivation
                  ? "info.lighter"
                  : "background.paper",
                opacity: unavailable ? 0.55 : 1,
                transition: "all 0.15s",
                "&:hover": unavailable
                  ? undefined
                  : { borderColor: `${tier.color}.light`, transform: "translateY(-2px)" },
                height: "100%",
              }}
            >
              {isCurrent && (
                <Chip
                  size="small"
                  color="success"
                  label="Plan actual"
                  sx={{
                    position: "absolute",
                    top: 8,
                    left: 8,
                    fontWeight: 800,
                    height: 20,
                    fontSize: 10,
                  }}
                />
              )}
              {tier.badge && !isCurrent && !isPendingActivation && (
                <Chip
                  size="small"
                  color="warning"
                  label={tier.badge}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    fontWeight: 700,
                    height: 20,
                    fontSize: 10,
                  }}
                />
              )}
              {isPendingActivation && (
                <Chip
                  size="small"
                  color="info"
                  icon={<ScheduleRoundedIcon sx={{ fontSize: 12 }} />}
                  label={`Empieza ${formatPendingDate(pendingAt)}`}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    fontWeight: 800,
                    height: 20,
                    fontSize: 10,
                    "& .MuiChip-icon": { ml: 0.5 },
                  }}
                />
              )}
              <CardContent sx={{ p: 2.5, height: "100%", display: "flex", flexDirection: "column" }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 1.5,
                      bgcolor: selected ? `${tier.color}.main` : `${tier.color}.lighter`,
                      color: selected ? "#fff" : `${tier.color}.dark`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <IconComp fontSize="medium" />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      {tier.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {tier.tagline}
                    </Typography>
                  </Box>
                  {selected && (
                    <CheckCircleRoundedIcon sx={{ color: `${tier.color}.main` }} />
                  )}
                </Stack>

                <Stack spacing={0.5} sx={{ flex: 1, mb: 1.5 }}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    <strong>Canal:</strong> {tier.channel}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    <strong>Respuesta:</strong> {tier.responseTime}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    <strong>Horario:</strong> {tier.hours}
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    pt: 1,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    textAlign: "right",
                  }}
                >
                  {tier.key === "basic" ? (
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "success.dark" }}>
                      Gratis
                    </Typography>
                  ) : unavailable ? (
                    <Typography variant="caption" sx={{ color: "text.disabled", fontStyle: "italic" }}>
                      Próximamente
                    </Typography>
                  ) : (
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: `${tier.color}.dark` }}>
                      +{Number(price).toFixed(0)} €/mes
                    </Typography>
                  )}
                </Box>

                {isPendingCancel && tier.kind === pendingCancelKind && (
                  <Box
                    sx={{
                      mt: 1,
                      pt: 1,
                      borderTop: "1px dashed",
                      borderColor: "warning.light",
                    }}
                  >
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <ScheduleRoundedIcon sx={{ fontSize: 14, color: "warning.dark" }} />
                      <Typography variant="caption" sx={{ color: "warning.darker", fontWeight: 700 }}>
                        Termina el {formatPendingDate(pendingAt)}
                      </Typography>
                    </Stack>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}

SupportPicker.propTypes = {
  /** kind del add-on de soporte que el user PAGA hoy (radio-selected). */
  value: PropTypes.oneOf([null, "support-standard", "support-priority"]),
  /** (newKind: string | null) => void */
  onChange: PropTypes.func.isRequired,
  /** kind que se activará en `pendingAt` (por downgrade programado). */
  pendingActivationKind: PropTypes.oneOf([null, "support-standard", "support-priority"]),
  /** kind actual que se cancelará en `pendingAt`. */
  pendingCancelKind: PropTypes.oneOf([null, "support-standard", "support-priority"]),
  /** Fecha del cambio programado (ISO string o Date). */
  pendingAt: PropTypes.any,
  /** Si true, muestra chip "Plan actual" en la tarjeta seleccionada. */
  isCurrentPaid: PropTypes.bool,
  /** Ciclo del plan del user — decide qué precio pintar en Estándar/Prioritario. */
  cycle: PropTypes.oneOf(["monthly", "yearly"]),
};
